import { isAfter, parse, subDays } from 'date-fns'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'

import { Speaker, TimelineChannel } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { CHAT_TRANSCRIPT_MEDIUM, CHAT_TRANSCRIPT_TIMEZONE } from '../../StayTimeline.consts'
import { GuestInboxTimelineEvent } from '../../StayTimeline.types'
import { toDate } from '../../StayTimeline.utils'

type TranscriptTurn = GuestInboxTimelineEvent['turns'][number]

const isChatTranscript = (event: GuestInboxTimelineEvent) => {
  return (
    event.channel === TimelineChannel.zendesk &&
    event.medium === CHAT_TRANSCRIPT_MEDIUM &&
    event.turns.length > 0
  )
}

/** The `(6:59:12 PM) ` stamp that opens each turn in the body; the author's name follows it. */
const STAMP_PATTERN = /\((\d{1,2}:\d{2}:\d{2} [AP]M)\) /g

/** Every stamp in the body, in order, with where the author's name after it starts. */
const findStamps = (body: string) => {
  return Array.from(body.matchAll(STAMP_PATTERN), match => ({
    stamp: match[1],
    nameIndex: match.index + match[0].length,
  }))
}

/**
 * Each turn's stamp: the next stamp in the body followed by the turn's author. Walking the stamps
 * in order makes a speaker's repeated turns take their own stamps, not the first one.
 */
interface GetTurnStampsParams {
  body: string
  turns: TranscriptTurn[]
}

const getTurnStamps = ({ body, turns }: GetTurnStampsParams) => {
  const stamps = findStamps(body)
  let nextStamp = 0

  return turns.map(turn => {
    const index = stamps.findIndex((stamp, stampIndex) => {
      return stampIndex >= nextStamp && body.startsWith(`${turn.who}:`, stamp.nameIndex)
    })

    if (index < 0) return undefined

    nextStamp = index + 1
    return stamps[index].stamp
  })
}

/**
 * When a stamped turn was sent: its stamp read in Zendesk's timezone, on the event's day there.
 * A stamp later than the event was sent the day before (the chat crossed midnight).
 */
interface GetSentAtParams {
  eventAt: Date
  stamp: string
}

const getSentAt = ({ eventAt, stamp }: GetSentAtParams) => {
  const day = formatInTimeZone(eventAt, CHAT_TRANSCRIPT_TIMEZONE, 'yyyy-MM-dd')
  const wallTime = parse(`${day} ${stamp}`, 'yyyy-MM-dd h:mm:ss a', eventAt)
  const sentAt = zonedTimeToUtc(wallTime, CHAT_TRANSCRIPT_TIMEZONE)

  return isAfter(sentAt, eventAt) ? subDays(sentAt, 1) : sentAt
}

/**
 * Each turn's time from its stamp. A turn without a stamp takes the time of the turn before it;
 * without any stamp, every turn takes the event's time.
 */
interface GetTurnTimesParams {
  eventAt: Date
  stamps: (string | undefined)[]
}

const getTurnTimes = ({ eventAt, stamps }: GetTurnTimesParams) => {
  const times = stamps.map(stamp => {
    return stamp ? getSentAt({ eventAt, stamp }) : undefined
  })

  let previous = times.find(Boolean) ?? eventAt
  return times.map(time => (previous = time ?? previous))
}

interface SplitChatTranscriptParams {
  event: GuestInboxTimelineEvent
  guestName?: string | null
}

export const splitChatTranscript = ({
  event,
  guestName,
}: SplitChatTranscriptParams): GuestInboxTimelineEvent[] => {
  const eventAt = toDate(event.at)

  if (!isChatTranscript(event) || !eventAt || !guestName) return [event]

  const stamps = getTurnStamps({ body: event.body, turns: event.turns })
  const times = getTurnTimes({ eventAt, stamps })

  return event.turns.map((turn, index) => ({
    ...event,
    id: `${event.id}:turn-${index}`,
    at: times[index].toISOString(),
    speaker: turn.who === guestName ? Speaker.guest : Speaker.teammate,
    who: turn.who,
    body: turn.text,
    turns: [],
  }))
}
